import { BE_DEFINES } from 'constants/backend-defines.const';
import { LAYOUT_TYPES, SHOOTING_TYPES } from 'constants/booth.const';
import { STATUS_TYPES } from 'constants/common.const';
import { MEDIA_TYPES } from 'constants/file.const';
import { I18nNamespace, Languages } from 'constants/i18n.const';
import { ChatBotBoothType } from 'models/booth/booth.model';
import { CriterialType } from 'models/feedback/feedback.model';
import { DaySinceFromTransactionTypes } from 'models/setting/member.model';
import { ZNSTypes } from 'models/setting/message-template/message-template.model';
import { PhotoFilterEnum } from 'models/setting/photo-filter/photo-filter.model';
import {
  IsRefundEnum,
  PaymentMethodEnum,
  SatisfactionLevelEnum,
} from 'models/transaction/transaction.model';
import React, { useMemo } from 'react';
import { EPaymentMethodTypes } from 'models/store/store.model';
import { AssetIcon } from 'assets/icon/Icons';
import i18n from 'i18n';
import { useTranslation } from './useTranslation';

export const useOptions = () => {
  const { T } = useTranslation(I18nNamespace.COMMON);
  const layoutTemplateOptions = useMemo(
    () => [
      {
        key: LAYOUT_TYPES['264A'],
        text: LAYOUT_TYPES['264A'],
        value: LAYOUT_TYPES['264A'],
      },
      {
        key: LAYOUT_TYPES['466B'],
        text: LAYOUT_TYPES['466B'],
        value: LAYOUT_TYPES['466B'],
      },
    ],
    [],
  );

  const statusOptions = useMemo(
    () => [
      {
        key: STATUS_TYPES.ACTIVE,
        text: T('active'),
        value: STATUS_TYPES.ACTIVE,
      },
      {
        key: STATUS_TYPES.INACTIVE,
        text: T('inactive'),
        value: STATUS_TYPES.INACTIVE,
      },
    ],
    [T],
  );

  const annualRevenueStoreOptions = useMemo(
    () => [
      {
        key: STATUS_TYPES.ACTIVE,
        text: T('highestRevenue'),
        value: STATUS_TYPES.ACTIVE,
      },
      {
        key: STATUS_TYPES.INACTIVE,
        text: T('lowestRevenue'),
        value: STATUS_TYPES.INACTIVE,
      },
    ],
    [T],
  );

  const voucherStatusOptions = useMemo(
    () => [
      {
        key: STATUS_TYPES.ACTIVE,
        text: T('used'),
        value: STATUS_TYPES.ACTIVE,
      },
      {
        key: STATUS_TYPES.INACTIVE,
        text: T('unused'),
        value: STATUS_TYPES.INACTIVE,
      },
    ],
    [T],
  );

  const captureModeOptions = useMemo(
    () => [
      {
        key: SHOOTING_TYPES.ERROR,
        text: T('error'),
        value: SHOOTING_TYPES.ERROR,
      },
      {
        key: SHOOTING_TYPES.COUNTDOWN,
        text: T('countdown'),
        value: SHOOTING_TYPES.COUNTDOWN,
      },
      {
        key: SHOOTING_TYPES.BY_REMOTE,
        text: T('remote'),
        value: SHOOTING_TYPES.BY_REMOTE,
      },
    ],
    [T],
  );

  const depositTypeOptions = useMemo(
    () => [
      {
        key: BE_DEFINES.DEPOSIT_MACHINE_TYPE.MONEY_IN,
        text: T('moneyIn'),
        value: BE_DEFINES.DEPOSIT_MACHINE_TYPE.MONEY_IN,
      },
      {
        key: BE_DEFINES.DEPOSIT_MACHINE_TYPE.MONEY_OUT,
        text: T('moneyOut'),
        value: BE_DEFINES.DEPOSIT_MACHINE_TYPE.MONEY_OUT,
      },
    ],
    [T],
  );

  const billAcceptorTypeOptions = useMemo(
    () => [
      {
        key: BE_DEFINES.BILL_ACCEPTOR_TYPE.ICT,
        text: T('ICT'),
        value: BE_DEFINES.BILL_ACCEPTOR_TYPE.ICT,
      },
      {
        key: BE_DEFINES.BILL_ACCEPTOR_TYPE.TOP,
        text: T('TOP'),
        value: BE_DEFINES.BILL_ACCEPTOR_TYPE.TOP,
      },
    ],
    [T],
  );

  const boothTypeOptions = useMemo(
    () => [
      {
        key: BE_DEFINES.BOOTH_TYPE.NORMAL,
        text: T('photoBoothNormal'),
        value: BE_DEFINES.BOOTH_TYPE.NORMAL,
      },
      {
        key: BE_DEFINES.BOOTH_TYPE.ROBOT_ARM,
        text: T('robotArmPhotoBooth'),
        value: BE_DEFINES.BOOTH_TYPE.ROBOT_ARM,
      },
      {
        key: BE_DEFINES.BOOTH_TYPE.OVER_HEAD,
        text: T('overheadPhotoBooth'),
        value: BE_DEFINES.BOOTH_TYPE.OVER_HEAD,
      },
    ],
    [T],
  );

  const boothScreenTypeOptions = useMemo(
    () => [
      {
        key: BE_DEFINES.SCREEN_TYPE.HORIZONTAL,
        text: T('horizontalScreen'),
        value: BE_DEFINES.SCREEN_TYPE.HORIZONTAL,
      },
      {
        key: BE_DEFINES.SCREEN_TYPE.VERTICAL,
        text: T('verticalScreen'),
        value: BE_DEFINES.SCREEN_TYPE.VERTICAL,
      },
    ],
    [T],
  );

  const boothVersionTypeOptions = useMemo(
    () => [
      {
        key: BE_DEFINES.BOOTH_VERSION_TYPE.UPDATE_SYNC,
        text: T('syncService'),
        value: BE_DEFINES.BOOTH_VERSION_TYPE.UPDATE_SYNC,
      },
      {
        key: BE_DEFINES.BOOTH_VERSION_TYPE.UPDATE_BE_API,
        text: T('syncBEApi'),
        value: BE_DEFINES.BOOTH_VERSION_TYPE.UPDATE_BE_API,
      },
    ],
    [T],
  );

  const paymentMethodOptions = useMemo(
    () => [
      {
        key: PaymentMethodEnum.CASH,
        text: T(`${I18nNamespace.COMMON}:cash`),
        value: PaymentMethodEnum.CASH,
      },
      {
        key: PaymentMethodEnum.MOMO,
        text: T(`${I18nNamespace.COMMON}:momoBanking`),
        value: PaymentMethodEnum.MOMO,
      },
      {
        key: PaymentMethodEnum.TEST_PC,
        text: T(`${I18nNamespace.COMMON}:testPC`),
        value: PaymentMethodEnum.TEST_PC,
      },
      {
        key: PaymentMethodEnum.TEST_FRAME,
        text: T(`${I18nNamespace.COMMON}:testFrame`),
        value: PaymentMethodEnum.TEST_FRAME,
      },
      {
        key: PaymentMethodEnum.RETAKE_PHOTO,
        text: T(`${I18nNamespace.COMMON}:retakePhoto`),
        value: PaymentMethodEnum.RETAKE_PHOTO,
      },
      {
        key: PaymentMethodEnum.VNPAY,
        text: T(`${I18nNamespace.COMMON}:vnPayBanking`),
        value: PaymentMethodEnum.VNPAY,
      },
    ],
    [T],
  );

  const satisficationOptions = useMemo(
    () => [
      {
        key: SatisfactionLevelEnum.VERYSATISFIED,
        text: T(`${I18nNamespace.COMMON}:satisficationLevel.verysatisfied`),
        value: SatisfactionLevelEnum.VERYSATISFIED,
      },
      {
        key: SatisfactionLevelEnum.SATISFIED,
        text: T(`${I18nNamespace.COMMON}:satisficationLevel.satisfied`),
        value: SatisfactionLevelEnum.SATISFIED,
      },
      {
        key: SatisfactionLevelEnum.NORMAL,
        text: T(`${I18nNamespace.COMMON}:satisficationLevel.normal`),
        value: SatisfactionLevelEnum.NORMAL,
      },
      {
        key: SatisfactionLevelEnum.BAD,
        text: T(`${I18nNamespace.COMMON}:satisficationLevel.bad`),
        value: SatisfactionLevelEnum.BAD,
      },
      {
        key: SatisfactionLevelEnum.VERYBAD,
        text: T(`${I18nNamespace.COMMON}:satisficationLevel.verybad`),
        value: SatisfactionLevelEnum.VERYBAD,
      },
    ],
    [T],
  );

  const photoFilterTypeOptions = useMemo(
    () => [
      {
        key: PhotoFilterEnum.FUN,
        text: 'FunStudio',
        value: PhotoFilterEnum.FUN,
      },
      {
        key: PhotoFilterEnum.FACE_BEAUTY_PRO,
        text: 'Face Beauty Pro',
        value: PhotoFilterEnum.FACE_BEAUTY_PRO,
      },
      {
        key: PhotoFilterEnum.FACE_BEAUTY,
        text: 'Face Beauty',
        value: PhotoFilterEnum.FACE_BEAUTY,
      },
      {
        key: PhotoFilterEnum.SMART_BEAUTY,
        text: 'Smart Beauty',
        value: PhotoFilterEnum.SMART_BEAUTY,
      },
      {
        key: PhotoFilterEnum.OPENCV_BEAUTY,
        text: 'OpenCV Beauty',
        value: PhotoFilterEnum.OPENCV_BEAUTY,
      },
      {
        key: PhotoFilterEnum.DEEP_BEAUTY_IMAGE,
        text: 'Deep Beauty Image',
        value: PhotoFilterEnum.DEEP_BEAUTY_IMAGE,
      },
    ],
    [],
  );

  const refundAmountOptions = useMemo(
    () => [
      {
        key: IsRefundEnum.YES,
        text: T(`${I18nNamespace.COMMON}:haveRefundAmount`),
        value: IsRefundEnum.YES,
      },
      {
        key: IsRefundEnum.NO,
        text: T(`${I18nNamespace.COMMON}:notHaveRefundAmount`),
        value: IsRefundEnum.NO,
      },
    ],
    [T],
  );

  const chatbotTypeOptions = useMemo(
    () => [
      {
        key: ChatBotBoothType.FUN_ADMIN,
        text: T(`${I18nNamespace.COMMON}:funAdmin`),
        value: ChatBotBoothType.FUN_ADMIN,
      },
      {
        key: ChatBotBoothType.TELEGRAM,
        text: T(`${I18nNamespace.COMMON}:telegram`),
        value: ChatBotBoothType.TELEGRAM,
      },
      {
        key: ChatBotBoothType.DISCORD,
        text: T(`${I18nNamespace.COMMON}:discord`),
        value: ChatBotBoothType.DISCORD,
      },
    ],
    [T],
  );

  const genderOptions = useMemo(
    () => [
      {
        text: T(`${I18nNamespace.COMMON}:male`),
        value: BE_DEFINES.GENDER.MALE,
      },
      {
        text: T(`${I18nNamespace.COMMON}:female`),
        value: BE_DEFINES.GENDER.FEMALE,
      },
    ],
    [T],
  );

  const criteriaTypeOptions = useMemo(
    () => [
      {
        key: CriterialType.RANKING,
        text: T(`${I18nNamespace.COMMON}:ctiteriaType.ranking`),
        value: CriterialType.RANKING,
      },
      {
        key: CriterialType.QUESTION,
        text: T(`${I18nNamespace.COMMON}:ctiteriaType.multipleChoice`),
        value: CriterialType.QUESTION,
      },
    ],
    [T],
  );

  const mediaTypeOptions = useMemo(
    () => [
      {
        key: MEDIA_TYPES.IMAGE,
        text: T(`${I18nNamespace.COMMON}:image`),
        value: MEDIA_TYPES.IMAGE,
      },
      {
        key: MEDIA_TYPES.VIDEO,
        text: T(`${I18nNamespace.COMMON}:video`),
        value: MEDIA_TYPES.VIDEO,
      },
      {
        key: MEDIA_TYPES.GIF,
        text: T(`${I18nNamespace.COMMON}:gif`),
        value: MEDIA_TYPES.GIF,
      },
    ],
    [T],
  );

  const starOptions = useMemo(
    () => [
      { key: 1, value: 1, text: '⭐' },
      { key: 2, value: 2, text: '⭐⭐' },
      { key: 3, value: 3, text: '⭐⭐⭐' },
      { key: 4, value: 4, text: '⭐⭐⭐⭐' },
      { key: 5, value: 5, text: '⭐⭐⭐⭐⭐' },
    ],
    [],
  );

  const ZNSOptions = useMemo(
    () => [
      {
        key: ZNSTypes.RemindComeBack,
        text: T(`${I18nNamespace.COMMON}:ZNSTypes.RemindComeBack`),
        value: ZNSTypes.RemindComeBack,
      },
      {
        key: ZNSTypes.BirthDayMessage,
        text: T(`${I18nNamespace.COMMON}:ZNSTypes.BirthDayMessage`),
        value: ZNSTypes.BirthDayMessage,
      },
      {
        key: ZNSTypes.RemaindExchangePoint,
        text: T(`${I18nNamespace.COMMON}:ZNSTypes.RemaindExchangePoint`),
        value: ZNSTypes.RemaindExchangePoint,
      },
      {
        key: ZNSTypes.OtpMessage,
        text: T(`${I18nNamespace.COMMON}:ZNSTypes.OtpMessage`),
        value: ZNSTypes.OtpMessage,
      },
    ],
    [T],
  );

  // options lọc ngày gần nhất giao dịch
  const daysSinceLastTransactionFilterOptions = useMemo(
    () => [
      {
        key: DaySinceFromTransactionTypes.NOT_EXCEED_30_DAYS,
        text: T(`${I18nNamespace.COMMON}:notExceed30Days`),
        value: DaySinceFromTransactionTypes.NOT_EXCEED_30_DAYS,
      },
      {
        key: DaySinceFromTransactionTypes.FROM_30_TO_60_DAYS,
        text: T(`${I18nNamespace.COMMON}:30To60Days`),
        value: DaySinceFromTransactionTypes.FROM_30_TO_60_DAYS,
      },
      {
        key: DaySinceFromTransactionTypes.EXCEED_60_DAYS,
        text: T(`${I18nNamespace.COMMON}:exceed60Days`),
        value: DaySinceFromTransactionTypes.EXCEED_60_DAYS,
      },
    ],
    [T],
  );

  const ePaymentMethod = useMemo(
    () => [
      {
        key: EPaymentMethodTypes.MOMO,
        text: T(`${I18nNamespace.COMMON}:ePaymentMethod.momo`),
        value: EPaymentMethodTypes.MOMO,
      },
      {
        key: EPaymentMethodTypes.VN_PAY,
        text: T(`${I18nNamespace.COMMON}:ePaymentMethod.vnPay`),
        value: EPaymentMethodTypes.VN_PAY,
      },
    ],
    [T],
  );

  const languageOptions = useMemo(
    () => [
      {
        key: Languages.VIETNAM,
        label: T('language.vietnamese'),
        icon: <AssetIcon.VietnamIcon />,
      },
      {
        key: Languages.ENGLISH,
        label: T('language.english'),
        icon: <AssetIcon.EnglishIcon />,
      },
    ],
    [T],
  );

  const monthOptions = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => {
        const date = new Date(2024, i, 1);

        return {
          key: i + 1,
          text: date.toLocaleString(i18n.language, { month: 'long' }),
          value: i + 1,
        };
      }),
    [],
  );

  return {
    layoutTemplateOptions,
    statusOptions,
    voucherStatusOptions,
    captureModeOptions,
    depositTypeOptions,
    boothTypeOptions,
    boothScreenTypeOptions,
    boothVersionTypeOptions,
    paymentMethodOptions,
    photoFilterTypeOptions,
    annualRevenueStoreOptions,
    billAcceptorTypeOptions,
    refundAmountOptions,
    chatbotTypeOptions,
    genderOptions,
    criteriaTypeOptions,
    starOptions,
    satisficationOptions,
    ZNSOptions,
    daysSinceLastTransactionFilterOptions,
    mediaTypeOptions,
    ePaymentMethod,
    languageOptions,
    monthOptions,
  };
};
